'use client';

import { memo } from 'react';
import { Box, Stack, IconButton, Typography, alpha } from '@mui/material';
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from '@mui/icons-material';
import { themeColors } from '../theme/theme';

/**
 * ModernPagination - Clean, elegant pagination component
 * Features:
 * - Compact design
 * - First/Last page shortcuts
 * - Clear page indicator
 * - Smooth transitions
 */
const ModernPagination = memo(
  ({ page, totalPages, onChange, showFirstLast = true, size = 'medium' }) => {
    if (totalPages <= 1) return null;

    const handleChange = (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
        onChange(null, newPage);
      }
    };

    const buttonSize = size === 'small' ? 32 : 36;
    const iconSize = size === 'small' ? 18 : 20;

    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 1.5,
          px: 2,
          bgcolor: '#fff',
          borderRadius: 3,
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* First Page */}
        {showFirstLast && (
          <IconButton
            onClick={() => handleChange(1)}
            disabled={page === 1}
            sx={{
              width: buttonSize,
              height: buttonSize,
              borderRadius: 1.5,
              color: page === 1 ? '#d1d5db' : '#6b7280',
              '&:hover': {
                bgcolor: '#f3f4f6',
              },
              '&:disabled': {
                color: '#e5e7eb',
              },
            }}
          >
            <FirstPage sx={{ fontSize: iconSize }} />
          </IconButton>
        )}

        {/* Previous */}
        <IconButton
          onClick={() => handleChange(page - 1)}
          disabled={page === 1}
          sx={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: 1.5,
            color: page === 1 ? '#d1d5db' : '#6b7280',
            '&:hover': {
              bgcolor: '#f3f4f6',
            },
            '&:disabled': {
              color: '#e5e7eb',
            },
          }}
        >
          <ChevronLeft sx={{ fontSize: iconSize }} />
        </IconButton>

        {/* Page Indicator */}
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderRadius: 2,
            bgcolor: alpha(themeColors.primaryColor, 0.08),
            minWidth: 80,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: size === 'small' ? '0.75rem' : '0.8rem',
              fontWeight: 700,
              color: themeColors.primaryColor,
            }}
          >
            <Box component="span" sx={{ fontWeight: 800 }}>
              {page}
            </Box>
            <Box component="span" sx={{ mx: 0.5, color: '#9ca3af' }}>
              /
            </Box>
            <Box component="span" sx={{ color: '#6b7280' }}>
              {totalPages}
            </Box>
          </Typography>
        </Box>

        {/* Next */}
        <IconButton
          onClick={() => handleChange(page + 1)}
          disabled={page === totalPages}
          sx={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: 1.5,
            color: page === totalPages ? '#d1d5db' : '#6b7280',
            '&:hover': {
              bgcolor: '#f3f4f6',
            },
            '&:disabled': {
              color: '#e5e7eb',
            },
          }}
        >
          <ChevronRight sx={{ fontSize: iconSize }} />
        </IconButton>

        {/* Last Page */}
        {showFirstLast && (
          <IconButton
            onClick={() => handleChange(totalPages)}
            disabled={page === totalPages}
            sx={{
              width: buttonSize,
              height: buttonSize,
              borderRadius: 1.5,
              color: page === totalPages ? '#d1d5db' : '#6b7280',
              '&:hover': {
                bgcolor: '#f3f4f6',
              },
              '&:disabled': {
                color: '#e5e7eb',
              },
            }}
          >
            <LastPage sx={{ fontSize: iconSize }} />
          </IconButton>
        )}
      </Stack>
    );
  }
);

ModernPagination.displayName = 'ModernPagination';

export { ModernPagination };
export default ModernPagination;
